<template>
  <div
    class="vertical-layout h-100"
    :class="[layoutClasses]"
    :data-col="isNavMenuHidden ? '1-column' : null"
  >
    <!-- Navbar -->
    <b-navbar
      :toggleable="false"
      :variant="navbarBackgroundColor"
      class="header-navbar navbar navbar-shadow align-items-center"
      :class="[navbarTypeClass]"
    >
      <slot
        name="navbar"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :navbarBackgroundColor="navbarBackgroundColor"
        :navbarTypeClass="[...navbarTypeClass, 'header-navbar navbar navbar-shadow align-items-center']"
      >
        <app-navbar-vertical-layout :toggle-vertical-menu-active="toggleVerticalMenuActive" />
      </slot>
    </b-navbar>
    <!--/ Navbar -->

    <!-- Vertical Nav Menu -->
    <vertical-nav-menu
      v-if="!isNavMenuHidden"
      :is-vertical-menu-active="isVerticalMenuActive"
      :toggle-vertical-menu-active="toggleVerticalMenuActive"
    >
      <template #header="slotProps">
        <slot
          name="vertical-menu-header"
          v-bind="slotProps"
        />
      </template>
    </vertical-nav-menu>
    <!-- /Vertical Nav Menu -->

    <!-- Vertical Nav Menu Overlay -->
    <div
      class="sidenav-overlay"
      :class="overlayClasses"
      @click="isVerticalMenuActive = false"
    />
    <!-- /Vertical Nav Menu Overlay -->

    <!-- Content -->

    <!-- CONTENT TYPE: Left -->
    <transition
      :name="routerTransition"
      mode="out-in"
    >
      <component
        :is="layoutContentRenderer"
        :key="layoutContentRenderer === 'layout-content-renderer-left' ? $route.meta.navActiveLink || $route.name : null"
      >
        <template
          v-for="(index, name) in $scopedSlots"
          v-slot:[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
      </component>
    </transition>
    <!--/ Content -->

    <!-- Footer -->
    <footer
      class="footer footer-light"
      :class="[footerTypeClass]"
    >
      <slot name="footer">
        <app-footer />
      </slot>
    </footer>
    <!-- /Footer -->

    <slot name="customizer" />
  </div>
</template>

<script>
import { onMounted, onUnmounted } from '@vue/composition-api'
import AppNavbarVerticalLayout from '@core/layouts/components/app-navbar/AppNavbarVerticalLayout.vue'
import AppFooter from '@core/layouts/components/AppFooter.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import { BNavbar } from 'bootstrap-vue'
import LayoutContentRendererDefault from '@core/layouts/components/layout-content-renderer/LayoutContentRendererDefault.vue'
import LayoutContentRendererLeft from '@core/layouts/components/layout-content-renderer/LayoutContentRendererLeft.vue'
import LayoutContentRendererLeftDetached from '@core/layouts/components/layout-content-renderer/LayoutContentRendererLeftDetached.vue'
import VerticalNavMenu from './components/vertical-nav-menu/VerticalNavMenu.vue'
import useVerticalLayout from './useVerticalLayout'
import mixinVerticalLayout from './mixinVerticalLayout'
import moment from 'moment'
import chatbotAxios from '@/libs/chatbot-axios'
import axios from '@/libs/axios'
import store from '@/store'
import { getUserData } from '@/auth/utils'

export default {
  components: {
    // AppBreadcrumb,
    AppNavbarVerticalLayout,
    AppFooter,
    VerticalNavMenu,
    BNavbar,
    LayoutContentRendererLeftDetached,
    LayoutContentRendererLeft,
    LayoutContentRendererDefault,
  },
  mixins: [mixinVerticalLayout],
  computed: {
    layoutContentRenderer() {
      const rendererType = this.$route.meta.contentRenderer
      if (rendererType === 'sidebar-left') return 'layout-content-renderer-left'
      if (rendererType === 'sidebar-left-detached') return 'layout-content-renderer-left-detached'
      return 'layout-content-renderer-default'
    },
  },
  setup() {
    const {
      routerTransition, navbarBackgroundColor, navbarType, footerType, isNavMenuHidden,
    } = useAppConfig()

    const {
      isVerticalMenuActive,
      toggleVerticalMenuActive,
      isVerticalMenuCollapsed,
      layoutClasses,
      overlayClasses,
      resizeHandler,
      navbarTypeClass,
      footerTypeClass,
    } = useVerticalLayout(navbarType, footerType)

    // Resize handler
    resizeHandler()
    window.addEventListener('resize', resizeHandler)

    // Global notification poller — keeps header badge counts updated on all pages
    let unreadTimer = null
    const pollNotifications = () => {
      const userData = getUserData()
      // Matches the role gating on the dashboard stat tiles (EcommerceStatistics.vue)
      // so the bell only surfaces counts the user would actually see there.
      const isSuperAdmin = !!(userData && userData.role === 'Super Admin')
      const showLeaseTile = isSuperAdmin || !!(userData && userData.role === 'Sale')
      const showPropertyTile = !(userData && userData.role === 'Sale')

      // Chat unread count
      chatbotAxios.get('/api/v1/conversations').then(({ data }) => {
        const users = data.data || data
        const total = Array.isArray(users) ? users.reduce((sum, u) => sum + (u.unread_count || 0), 0) : 0
        store.commit('chatbot/SET_UNREAD', total)
      }).catch(() => {})

      // Pending property request count
      axios.post('v2/property/filter', JSON.stringify({ perpage: 1, offset: 0, is_approve: 0 })).then(({ data }) => {
        store.commit('notifications/SET_PENDING_PROPERTY', data.total || 0)
      }).catch(() => {})

      // Owner-updated property count (matches LogPropertyUserUpdate.vue's fetch)
      axios.post('v1/property/updated/list', {
        perPage: 10,
        currentPage: 1,
        order_by: 'created_at desc',
        is_approve: 0,
      }).then(({ data }) => {
        store.commit('notifications/SET_OWNER_UPDATE_PROPERTY', (data.pagination && data.pagination.totalRecords) || 0)
      }).catch(() => {})

      const expireStart = moment().startOf('day').format('YYYY-MM-DD')
      const expireEnd = moment().add(2, 'months').endOf('day').format('YYYY-MM-DD')

      // Property expiring within 2 months
      if (showPropertyTile) {
        axios.post('/property/v2/filter', {
          expire_start: expireStart,
          expire_end: expireEnd,
          perpage: 1,
          offset: 0,
        }).then(({ data }) => {
          store.commit('notifications/SET_EXPIRING_PROPERTY', data.total || 0)
        }).catch(() => {})
      } else {
        // Clear a stale count left over from a previous session/role in the same tab
        store.commit('notifications/SET_EXPIRING_PROPERTY', 0)
      }

      // Lease expiring within 2 months
      if (showLeaseTile) {
        const createdBy = isSuperAdmin ? '' : (userData ? userData.id : '')

        axios.post('/close-deal/v1/lists', JSON.stringify({
          perpage: 1,
          offset: 0,
          code: '',
          type_id: userData ? userData.type_id : '',
          created_by: createdBy,
          action: 'Rent',
          expireStartDate: expireStart,
          expireEndDate: expireEnd,
          month: '',
          year: '',
        })).then(({ data }) => {
          store.commit('notifications/SET_LEASE_EXPIRING', data.total || 0)
        }).catch(() => {})
      } else {
        // Clear a stale count left over from a previous session/role in the same tab
        store.commit('notifications/SET_LEASE_EXPIRING', 0)
      }
    }
    onMounted(() => {
      pollNotifications()
      unreadTimer = setInterval(pollNotifications, 30000)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', resizeHandler)
      clearInterval(unreadTimer)
    })

    return {
      isVerticalMenuActive,
      toggleVerticalMenuActive,
      isVerticalMenuCollapsed,
      overlayClasses,
      layoutClasses,
      navbarTypeClass,
      footerTypeClass,

      // App Config
      routerTransition,
      navbarBackgroundColor,
      isNavMenuHidden,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/themes/bordered-layout.scss";
</style>
